import { PAGE_SIZE } from '../ui/molecules/ApiTable'
import { action, makeObservable, observable } from 'mobx'
import { CancellablePromise } from '@linqpal/models/src/helpers/caller'
import { exportToExcel } from '@linqpal/models/src/helpers/exportUtilss'

interface ISortOptions {
  column: string
  direction: string
}

export abstract class BaseTableStore<TTableItem, TFilter = null> {
  loading = false

  exportLoading = false

  dataLoaded = false // true after table data is loaded. Can be reset to force table refresh when table tab is focused

  page = 1

  sort: ISortOptions = {
    column: 'createdAt',
    direction: 'desc',
  }

  filter: TFilter | null = null

  tableData: {
    items: TTableItem[]
    totalCount: 0
  } = { items: [], totalCount: 0 }

  request: CancellablePromise<any> | undefined

  exportFileName = ''

  setPage(page: number) {
    this.page = page
    this.fetchData()
  }

  resetPage() {
    this.page = 1
  }

  setSort(column: string, direction: string) {
    this.sort = {
      column,
      direction,
    }
    this.fetchData()
  }

  resetSort() {
    this.sort = {
      column: 'createdAt',
      direction: 'desc',
    }
  }

  setFilter(filter: TFilter) {
    this.filter = filter
    this.fetchData()
  }

  initObservable() {
    makeObservable(this, {
      loading: observable,
      page: observable,
      sort: observable,
      tableData: observable,
      filter: observable,

      setPage: action,
      setSort: action,
      setFilter: action,
      fetchData: action,
    })
  }

  abstract callApi(params: any): CancellablePromise<any>

  abstract formatTableItem(dataItem: any): TTableItem

  abstract formatExportItem(
    tableItem: TTableItem,
  ): { [key: string]: string } | null

  fetchData() {
    this.loading = true

    if (this.request?.cancel) {
      this.request?.cancel()
    }

    this.request = this.callApi({
      ...this.filter,
      sortColumn: this.sort.column,
      sortDirection: this.sort.direction,
      page: this.page,
      pageSize: PAGE_SIZE,
    })

    this.request
      .then((response) => {
        this.dataLoaded = true

        if (!response?.items?.length && this.page !== 1) {
          this.tableData = {
            items: [],
            totalCount: 0,
          }
          // ApiTable tracks current page internally, so it can mismatch, TBD
          this.setPage(1)
        } else {
          this.tableData = {
            items: response?.items?.map((item: any) =>
              this.formatTableItem(item),
            ),
            totalCount: response?.totalCount,
          }
        }

        this.loading = false
      })
      .catch((e) => {
        // no need to reset loading on canceled requests - it will force APITable to display previous data
        if (e.code !== 'request-cancelled') {
          this.loading = false
          this.dataLoaded = true
          throw e
        }
      })
  }

  invalidate() {
    this.dataLoaded = false
  }

  async exportData() {
    this.exportLoading = true
    const response = await this.callApi({
      ...this.filter,
      page: -1,
      pageSize: -1,
    })

    const exportData = response.items
      ?.map((item: any) => {
        const formattedItem = this.formatTableItem(item)
        return this.formatExportItem(formattedItem)
      })
      .filter(Boolean)

    exportToExcel(exportData, this.exportFileName, 'xlsx')

    this.exportLoading = false
  }
}
